import { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';

interface ConnectionStatusProps {
  isConnected: boolean;
  isReconnecting?: boolean;
  isOnline?: boolean;
  className?: string;
}

export function ConnectionStatus({
  isConnected,
  isReconnecting = false,
  isOnline = true,
  className = '',
}: ConnectionStatusProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [wasDisconnected, setWasDisconnected] = useState(false);

  // Show banner when disconnected
  useEffect(() => {
    if (!isConnected || !isOnline) {
      setShowBanner(true);
      setWasDisconnected(true);
    } else if (wasDisconnected) {
      // Show "reconnected" message briefly
      const timer = setTimeout(() => {
        setShowBanner(false);
        setWasDisconnected(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isConnected, isOnline, wasDisconnected]);

  if (!showBanner) return null;

  // Offline (no internet)
  if (!isOnline) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-red-600 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg ${className}`}
      >
        <CloudOff className="size-5 flex-shrink-0" />
        <span className="text-sm font-medium">
          No internet connection. Your place in queue is saved.
        </span>
      </div>
    );
  }

  // Reconnecting
  if (isReconnecting) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-yellow-600 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg ${className}`}
      >
        <RefreshCw className="size-5 animate-spin flex-shrink-0" />
        <span className="text-sm font-medium">Reconnecting...</span>
      </div>
    );
  }

  // Disconnected from server
  if (!isConnected) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-yellow-600 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg ${className}`}
      >
        <WifiOff className="size-5 flex-shrink-0" />
        <span className="text-sm font-medium">
          Connection lost. Attempting to reconnect...
        </span>
      </div>
    );
  }

  // Just reconnected
  if (wasDisconnected && isConnected && isOnline) {
    return (
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-emerald-600 text-white px-4 py-3 flex items-center justify-center gap-3 shadow-lg animate-pulse ${className}`}
      >
        <Wifi className="size-5 flex-shrink-0" />
        <span className="text-sm font-medium">Connection restored</span>
      </div>
    );
  }

  return null;
}

// Inline status indicator (for use in headers/footers)
export function ConnectionIndicator({
  isConnected,
  isReconnecting = false,
  isOnline = true,
  className = '',
}: ConnectionStatusProps) {
  if (!isOnline) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <CloudOff className="size-4 text-red-400" />
        <span className="text-sm text-red-400">Offline</span>
      </div>
    );
  }

  if (isReconnecting) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <RefreshCw className="size-4 text-yellow-400 animate-spin" />
        <span className="text-sm text-yellow-400">Reconnecting</span>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <WifiOff className="size-4 text-yellow-400" />
        <span className="text-sm text-yellow-400">Disconnected</span>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Wifi className="size-4 text-emerald-400" />
      <span className="text-sm text-emerald-400">Connected</span>
    </div>
  );
}
